import { useState } from 'react'
import { Save, Pencil, X, Check, Copy } from 'lucide-react'
import { RewriteContent } from '@/services/api'
import clsx from 'clsx'

interface FinalizedTabProps {
  contents: RewriteContent[]
  activeChannelId: string | null
  activeSectionIndex: number
  onUpdateContent: (id: string, data: { sections?: Array<{ type: string; title: string; body: string }>; status?: string }) => Promise<void>
  copiedId: string | null
  onCopy: (text: string, id: string) => void
}

export function FinalizedTab({
  contents, activeChannelId, activeSectionIndex,
  onUpdateContent, copiedId, onCopy,
}: FinalizedTabProps) {
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState('')

  // Find confirmed or latest content for this channel
  const channelContents = contents
    .filter(c => c.channel_id === activeChannelId)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

  const confirmed = channelContents.find(c => c.status === 'published') || channelContents[0]
  if (!confirmed) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-text-tertiary p-6">
        <Check className="w-12 h-12 mb-3 opacity-30" />
        <p className="text-sm font-medium">확정된 콘텐츠가 없습니다</p>
        <p className="text-xs mt-1">AI 생성 후 확정해주세요</p>
      </div>
    )
  }

  const sections = typeof confirmed.sections === 'string' ? JSON.parse(confirmed.sections) : confirmed.sections
  const sectionArr = sections as Array<{ type: string; title: string; body: string }>
  const section = sectionArr?.[activeSectionIndex]
  if (!section) {
    return <div className="p-4 text-sm text-text-tertiary">해당 섹션이 없습니다.</div>
  }

  const sectionId = `finalized-${confirmed.id}-${activeSectionIndex}`

  const startEdit = () => {
    setEditText(section.body)
    setEditing(true)
  }

  const saveEdit = async () => {
    const updated = [...sectionArr]
    updated[activeSectionIndex] = { ...updated[activeSectionIndex], body: editText }
    await onUpdateContent(confirmed.id, { sections: updated })
    setEditing(false)
  }

  const handleConfirm = async () => {
    if (confirmed.status !== 'published') {
      await onUpdateContent(confirmed.id, { status: 'published' })
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-bold text-text-secondary">{section.title}</h4>
          <span className={clsx('text-xs px-1.5 py-0.5 rounded font-medium',
            confirmed.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
          )}>
            {confirmed.status === 'published' ? '확정' : '미확정'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onCopy(section.body, sectionId)} className="p-1 rounded hover:bg-bg-elevated text-text-tertiary hover:text-text-primary" title="복사">
            {copiedId === sectionId ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          {!editing && (
            <button onClick={startEdit} className="p-1 rounded hover:bg-bg-elevated text-text-tertiary hover:text-text-primary" title="편집">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={10}
            className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm resize-y"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-tertiary">{editText.length.toLocaleString()}자</span>
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)} className="btn btn-ghost text-xs flex items-center gap-1"><X className="w-3 h-3" /> 취소</button>
              <button onClick={saveEdit} className="btn btn-primary text-xs flex items-center gap-1"><Save className="w-3 h-3" /> 저장</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-bg-elevated/50 rounded-lg p-3">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{section.body}</p>
          <p className="text-xs text-text-tertiary mt-2">{section.body.length.toLocaleString()}자</p>
        </div>
      )}

      {confirmed.status !== 'published' && (
        <button onClick={handleConfirm} className="w-full btn btn-primary flex items-center justify-center gap-2 py-2.5">
          <Check className="w-4 h-4" /> 이 섹션 확정
        </button>
      )}
    </div>
  )
}
